import { useMemo, useState } from 'react'

import type { EagleOverview } from '../api/types'
import { localeCorrente, t } from '../i18n/lingua'
import { scalaPulita } from './charts'
import { curva } from './GraficoSerie'
import { formatOra, taccheTempo, useLarghezza } from './widget/graficiEagle'

/**
 * Quanti parametri erano in allarme, intervallo per intervallo: **aree
 * impilate** per stato — non risponde alla base, poi critico, poi attenzione
 * — continue nel tempo, con la stessa curva morbida e lo stesso velo dei
 * grafici delle serie. L'altezza totale è il numero dei parametri in allarme
 * in quell'intervallo; gli intervalli senza allarmi valgono zero e la curva
 * ci passa, invece di lasciare un buco. I parametri «ok» non si impilano —
 * sono quasi tutti, e schiaccerebbero il resto — ma stanno nel riquadro al
 * passaggio del mouse.
 *
 * In pixel veri (ResizeObserver), come `GraficoSerie`. Tacche dei tempi a
 * ore tonde; il passaggio del mouse (o le frecce) aggancia l'intervallo e
 * mostra i numeri. `compatto`: niente assi, legenda né etichette — la sola
 * forma, per stare dentro un riquadro accanto ai numeri.
 */
export function GraficoStati({
  punti, passoS, ore, altezza, compatto = false,
}: {
  punti: EagleOverview['timeline']
  passoS: number
  ore: number
  altezza?: number
  compatto?: boolean
}) {
  const [contenitore, larghezza] = useLarghezza<HTMLDivElement>(compatto ? 240 : 720)
  const [hover, setHover] = useState<number | null>(null)
  const [puntatore, setPuntatore] = useState<{ x: number; y: number } | null>(null)
  const adesso = useMemo(() => Date.now(), [punti])

  // La linea del tempo densa: un valore per ogni intervallo della finestra,
  // zero dove il Control Plane non ha mandato niente (nessun allarme).
  const passoMs = passoS * 1000
  const t0 = Math.floor((adesso - ore * 3_600_000) / passoMs) * passoMs
  const t1 = Math.max(adesso, ...punti.map((p) => new Date(p.ts).getTime() + passoMs))
  const densa = useMemo(() => {
    const per = new Map<number, EagleOverview['timeline'][number]>()
    for (const p of punti) per.set(Math.floor(new Date(p.ts).getTime() / passoMs) * passoMs, p)
    const out: { ts: number; ok: number; warn: number; crit: number; down: number }[] = []
    for (let ts = t0; ts < t1; ts += passoMs) {
      const p = per.get(ts)
      out.push({ ts, ok: p?.ok ?? 0, warn: p?.warn ?? 0, crit: p?.crit ?? 0, down: p?.down ?? 0 })
    }
    return out
  }, [punti, passoMs, t0, t1])

  if (!punti.length || densa.length < 2) {
    return compatto
      ? <div ref={contenitore} className="gs-stati gs-stati--compatto gs-stati--vuoto"><span className="muted">{t('nessun allarme nel periodo')}</span></div>
      : <div ref={contenitore} className="gs-stati"><p className="muted" style={{ margin: '16px 0' }}>{t('Nessuna misura nel periodo.')}</p></div>
  }

  const W = Math.max(compatto ? 120 : 320, larghezza)
  const H = altezza ?? (compatto ? 48 : 180)
  const M = compatto ? { top: 3, right: 2, bottom: 3, left: 2 } : { top: 12, right: 12, bottom: 30, left: 36 }
  const plotW = W - M.left - M.right
  const plotH = H - M.top - M.bottom
  const somme = densa.map((p) => p.warn + p.crit + p.down)
  // Scala «pulita» a conteggi interi (0, 5, 10, 15…): le aree riempiono il
  // riquadro invece di fermarsi a metà sotto un massimo troppo tondo.
  const { tetto: massimo, passoY } = scalaPulita(Math.max(1, ...somme), true)
  const x = (ts: number) => M.left + ((ts + passoMs / 2 - t0) / (t1 - t0)) * plotW
  const y = (v: number) => M.top + plotH - (v / massimo) * plotH
  const base = M.top + plotH
  const tacche = compatto ? [] : Array.from({ length: Math.floor(massimo / passoY) + 1 }, (_, i) => i * passoY)
  const tempi = compatto ? [] : taccheTempo(t0, t1, ore)
  const nessuno = somme.every((s) => s === 0)

  // Le tre fasce impilate: ognuna fra la cumulata precedente e la propria.
  const strati: { stato: 'down' | 'crit' | 'warn'; sotto: number[]; sopra: number[] }[] = []
  let cumulo = densa.map(() => 0)
  for (const stato of ['down', 'crit', 'warn'] as const) {
    const sopra = densa.map((p, i) => cumulo[i] + p[stato])
    strati.push({ stato, sotto: cumulo, sopra })
    cumulo = sopra
  }
  const xy = (valori: number[]) => valori.map((v, i) => ({ x: x(densa[i].ts), y: y(v) }))
  const forme = strati.map((s) => {
    const su = xy(s.sopra)
    const giu = xy(s.sotto)
    const indietro = curva([...giu].reverse()).replace(/^M/, 'L')
    return { stato: s.stato, area: `${curva(su)} ${indietro} Z`, linea: curva(su) }
  })

  const aggancia = (px: number) => {
    let migliore = 0
    let distanza = Infinity
    densa.forEach((p, i) => {
      const d = Math.abs(x(p.ts) - px)
      if (d < distanza) { distanza = d; migliore = i }
    })
    return migliore
  }
  const inHover = hover != null ? densa[hover] : null
  const tooltipX = puntatore ? Math.min(Math.max(puntatore.x + 14, 0), Math.max(0, W - 230)) : 0
  const tooltipY = puntatore ? Math.max(0, puntatore.y - 12) : 0

  return (
    <div ref={contenitore} className={`gs-stati${compatto ? ' gs-stati--compatto' : ''}`}>
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="gs__svg"
        role="img"
        tabIndex={compatto ? undefined : 0}
        aria-label={t('Parametri in allarme per intervallo nelle ultime {ore} ore', { ore })}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect()
          const px = e.clientX - box.left
          setHover(aggancia(px))
          setPuntatore({ x: px, y: e.clientY - box.top })
        }}
        onMouseLeave={() => setHover(null)}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
          e.preventDefault()
          const n = densa.length
          const prossimo = hover == null ? n - 1 : Math.max(0, Math.min(n - 1, hover + (e.key === 'ArrowRight' ? 1 : -1)))
          setHover(prossimo)
          setPuntatore({ x: x(densa[prossimo].ts), y: M.top })
        }}
      >
        {tacche.map((v) => (
          <g key={v}>
            <line x1={M.left} x2={W - M.right} y1={y(v)} y2={y(v)} className={v === 0 ? 'gs__base' : 'gs__grid'} />
            <text x={M.left - 8} y={y(v) + 4} textAnchor="end" className="gs__tick">{v.toLocaleString(localeCorrente())}</text>
          </g>
        ))}
        {compatto && <line x1={M.left} x2={W - M.right} y1={base} y2={base} className="gs__base" />}
        {forme.map((f) => (
          <g key={f.stato}>
            <path d={f.area} className={`gs-stati__area gs-stati__area--${f.stato}`} />
            <path d={f.linea} className={`gs-stati__linea gs-stati__linea--${f.stato}`} />
          </g>
        ))}
        {tempi.map((tt) => (
          <g key={tt.ts}>
            <line x1={x(tt.ts) - (passoMs / 2 / (t1 - t0)) * plotW} x2={x(tt.ts) - (passoMs / 2 / (t1 - t0)) * plotW} y1={base} y2={base + 4} className="gs__grid" />
            <text x={x(tt.ts) - (passoMs / 2 / (t1 - t0)) * plotW} y={base + 16} textAnchor="middle" className="gs__tick">{tt.label}</text>
          </g>
        ))}
        {nessuno && !compatto && (
          <text x={M.left + plotW / 2} y={M.top + plotH / 2} textAnchor="middle" className="gs__tick">
            {t('nessun parametro in allarme nel periodo')}
          </text>
        )}
        {inHover && (
          <g>
            <line x1={x(inHover.ts)} x2={x(inHover.ts)} y1={M.top} y2={base} className="gs__cross" />
            <circle cx={x(inHover.ts)} cy={y(inHover.warn + inHover.crit + inHover.down)} r={4} className="gs-stati__punto" />
          </g>
        )}
      </svg>
      {inHover && puntatore && (
        <div className="gs__tooltip" style={{ left: tooltipX, top: tooltipY }} role="status">
          <strong>{formatIntervallo(inHover.ts, passoS)}</strong>
          <span className="gs__riga"><span className="gs__chiave gs__chiave--down" />{t('{n} non rispondono', { n: inHover.down })}</span>
          <span className="gs__riga"><span className="gs__chiave gs__chiave--crit" />{t('{n} critici', { n: inHover.crit })}</span>
          <span className="gs__riga"><span className="gs__chiave gs__chiave--warn" />{t('{n} in attenzione', { n: inHover.warn })}</span>
          <span className="muted">{t('{n} ok', { n: inHover.ok })}</span>
        </div>
      )}
      {!compatto && (
        <div className="gs__legenda">
          <span className="gs__voce"><span className="gs__chiave gs__chiave--down" />{t('non risponde')}</span>
          <span className="gs__voce"><span className="gs__chiave gs__chiave--crit" />{t('critico')}</span>
          <span className="gs__voce"><span className="gs__chiave gs__chiave--warn" />{t('attenzione')}</span>
        </div>
      )}
    </div>
  )
}

function formatIntervallo(ts: number, passoS: number): string {
  const da = new Date(ts)
  const a = ts + passoS * 1000
  return `${da.toLocaleDateString(localeCorrente(), { day: '2-digit', month: '2-digit' })} ${formatOra(da.getTime())} – ${formatOra(a)}`
}
